if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      abortController = undefined;
      onVariantChangeUnsubscriber = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];
      lineItemFieldSelector = '.product__block--line_item_property :is(input, textarea, select)';
      cartUpdateUnsubscriber = undefined;
      cachedVariants = null;
      cachedVariantsSource = null;
      variantDataAbortController = undefined;
      variantDataToken = null;
      mediaTimer = undefined;

      constructor() {
        super();
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get productId() {
        return this.getAttribute('data-product-id');
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get productForm() {
        return this.querySelector('form[is="product-form"]');
      }

      get quantityInput() {
        return this.querySelector('quantity-input input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();

        this.currentVariant = this.getSelectedVariant(this);
        if (this.currentVariant) {
          this.updateMedia(this.currentVariant);
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
        this.variantDataAbortController?.abort();
        this.abortController?.abort();
        clearInterval(this.mediaTimer);
      }

      initializeProductSwapUtility() {
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      /**
       * The picked option names and values, read from the current picker. Each option's name sits on its
       * [data-selected-swatch-value] label, whatever the picker type (buttons, swatches, dropdown).
       */
      getStandardSelectedOptions() {
        const picker = this.variantSelectors;
        if (!picker) return [];

        return Array.from(picker.querySelectorAll('.product-form__input'))
          .map((field) => {
            const name = field.querySelector('[data-selected-swatch-value]')?.dataset.selectedSwatchValue;
            const value = (field.querySelector('select option[selected]') || field.querySelector('input:checked'))
              ?.value;
            return name && value ? { name, value } : null;
          })
          .filter(Boolean);
      }

      /**
       * shopify:product:select: fires when an option changes, before the matching variant is known.
       * Settled by renderProductInfo() with the matched variant (or null when none matches).
       */
      dispatchStandardSelect() {
        const selectedOptions = this.getStandardSelectedOptions();
        if (!FoxTheme.StandardEvents || !this.productId || selectedOptions.length === 0) return null;

        return {
          selectedOptions,
          handles: FoxTheme.StandardEvents.dispatch(this, 'ProductSelectEvent', {
            product: {
              id: FoxTheme.StandardEvents.productGid(this.productId),
              title: this.dataset.productTitle || '',
              handle: this.dataset.productHandle || '',
            },
            selectedOptions,
          }),
        };
      }

      /**
       * A selection made before the previous one was answered supersedes it. The older event is settled
       * with the variant its own options map to (the variants cache knows it), so the rapid click is not
       * reported as a failure. A complete cache with no match resolves it with no variant; when the cache cannot answer (a product over 250 variants, a switch to another product), the fetch abort rejects it with AbortError.
       */
      settleSupersededSelect() {
        const pending = this.pendingStandardSelect;
        this.pendingStandardSelect = null;
        // The cache describes this product: a selection that switched to another product cannot be read from it
        if (!pending || pending.handles.settled || pending.swapsProduct) return;

        const values = pending.selectedOptions.map((option) => option.value);
        const variant = this.getVariantsCache()?.find((candidate) => this.matchesOptions(candidate, values));
        if (!variant) {
          // A cache holding every variant of the product knows that this combination does not exist
          if (this.isVariantsCacheComplete()) {
            pending.handles.resolve({ variant: null });
          }
          return;
        }

        pending.handles.resolve({
          variant: FoxTheme.StandardEvents.variantToStandard(
            { ...variant, title: variant.options.join(' / ') },
            pending.selectedOptions
          ),
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        // Belongs to this selection only: the next one, from the main picker, scrolls to its image again.
        this.keepsPageScroll = event.detail?.keepPageScroll === true;

        this.settleSupersededSelect();

        // A new selection supersedes any variant-data answer still in flight. Its options check is not
        // enough on its own: after a combined-listing swap the old picker, still holding the old
        // selection, stays in the DOM for 500ms.
        this.variantDataAbortController?.abort();
        this.variantDataToken = null;

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;
        const viewMode = this.dataset.viewMode || 'main-product';

        // Answer the click with what the variant JSON already knows, then let the fetch below settle
        // the rest. A combined-listing swap, or a product too large to cache, has nothing to answer
        // with yet and falls back to the pending state until the fetch resolves.
        const optimisticVariant = shouldSwapProduct ? null : this.getVariantFromCache();
        if (optimisticVariant) {
          this.applyOptimisticUpdate(optimisticVariant, productUrl);
        } else {
          const cacheIsComplete = !!this.getVariantsCache() && this.isVariantsCacheComplete();

          // A complete cache with no match confirms the combination doesn't exist, so the unavailable
          // state can be shown immediately rather than waiting on a fetch.
          if (!shouldSwapProduct && cacheIsComplete) {
            this.updateURL(productUrl, null);
            this.updateVariantInputs(null);
            this.setUnavailable();
          } else {
            this.disableButtons();
          }

          // A complete cache (under the 250-variant cap) that found no match means this combination
          // genuinely doesn't exist: fetchVariantData's round trip would only rediscover the same "no
          // variant" answer. Past the cap the cache can't tell "impossible" from "just not cached", so
          // the fetch still runs.
          if (!shouldSwapProduct && !cacheIsComplete) this.fetchVariantData(productUrl, selectedOptionValues);
        }

        // Reported once the page shows the picked variant
        const standardSelect = this.dispatchStandardSelect();
        this.pendingStandardSelect = standardSelect;
        if (standardSelect) standardSelect.swapsProduct = shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          standardSelect,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage, viewMode)
            : this.handleUpdateProductInfo(productUrl, viewMode),
        });
      }

      handleSwapProduct(productUrl, updateFullPage, viewMode) {
        return (html) => {
          const quickView = html.querySelector('#MainProduct-quick-view__content');
          if (quickView && viewMode === 'quick-view') {
            html = quickView.content.cloneNode(true);
          }
          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));

          this.updateURL(productUrl, variant?.id);

          // The swap renders the new product's fields from scratch: carry over what the buyer already entered.
          const savedProperties = this.captureLineItemProperties(
            updateFullPage ? document.querySelector('main') : this
          );
          const postProcessHtmlCallbacks = [
            ...this.postProcessHtmlCallbacks,
            (newNode) => this.restoreLineItemProperties(newNode, savedProperties),
          ];

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;
            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              postProcessHtmlCallbacks
            );
            HTMLUpdateUtility.viewTransition(
              document.getElementById('shopify-section-sticky-atc-bar'),
              html.getElementById('shopify-section-sticky-atc-bar'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );

            if (!variant) {
              // This instance may already be unavailable, but the page it renders now is a new one.
              this.isUnavailable = false;
              this.setUnavailable();
              return;
            }
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              postProcessHtmlCallbacks
            );
          }

          this.currentVariant = variant;
          return variant;
        };
      }

      // Snapshots what the buyer entered in the line item property fields, keyed by "<type>:<name>". Hidden inputs are
      // skipped: the checkbox's companion input must keep the value of the new HTML.
      captureLineItemProperties(root) {
        const saved = new Map();

        root?.querySelectorAll(this.lineItemFieldSelector).forEach((field) => {
          if (!field.name || field.type === 'hidden') return;

          const key = `${field.type}:${field.name}`;
          if (field.type === 'checkbox') {
            saved.set(key, field.checked);
          } else if (field.type === 'radio') {
            if (field.checked) saved.set(key, field.value);
          } else {
            saved.set(key, field.value);
          }
        });

        return saved;
      }

      // Re-applies the snapshot on the new content. A field that no longer exists, changed type or lost the chosen
      // option keeps the new product's default.
      restoreLineItemProperties(root, saved) {
        if (!saved.size) return;

        root.querySelectorAll(this.lineItemFieldSelector).forEach((field) => {
          const key = `${field.type}:${field.name}`;
          if (!field.name || !saved.has(key)) return;
          const value = saved.get(key);

          if (field.type === 'checkbox') {
            field.checked = value === true;
          } else if (field.type === 'radio') {
            if (field.value === value) field.checked = true;
          } else if (field.tagName === 'SELECT') {
            // Assigning a missing value would leave the select blank.
            if (Array.from(field.options).some((option) => option.value === value)) field.value = value;
          } else if (typeof value === 'string') {
            field.value = value;
          }
        });
      }

      handleUpdateProductInfo(productUrl, viewMode) {
        return (html) => {
          const quickView = html.querySelector('#MainProduct-quick-view__content');
          if (quickView && viewMode === 'quick-view') {
            html = quickView.content.cloneNode(true);
          }
          const variant = this.getSelectedVariant(html);

          // The render settles everything a pending variant-data answer would; arriving after it, that
          // answer would only patch and publish the same variant a second time.
          this.variantDataAbortController?.abort();
          this.variantDataToken = null;

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateShareUrl(variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.isUnavailable = false;
          this.updateMedia(variant, html);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              // Price, SKU and inventory are role="status" regions the optimistic patch has usually
              // written already: rewriting the same content would have them announced a second time.
              // The badges too, which would otherwise be rebuilt for nothing.
              const isPatchedOptimistically = ['price', 'Sku', 'Inventory', 'Badges'].includes(id);
              if (
                !isPatchedOptimistically ||
                this.getRenderedSignature(source) !== this.getRenderedSignature(destination)
              ) {
                destination.innerHTML = source.innerHTML;
              }
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Badges');
          updateSourceFromDestination('PricePerItem');
          updateSourceFromDestination('Volume');

          this.updateQuantityRules(this.sectionId, this.productId, html);
          updateSourceFromDestination('QuantityRules');
          updateSourceFromDestination('VolumeNote');

          HTMLUpdateUtility.viewTransition(
            document.querySelector(`#SizeChart-${this.sectionId}`),
            html.querySelector(`#SizeChart-${this.sectionId}`),
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks
          );

          const stickyAtcBar = document.getElementById(`shopify-section-sticky-atc-bar`);
          if (stickyAtcBar) {
            stickyAtcBar.classList.remove('hidden');
          }

          const newAddButton = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
          const isDisabled = !newAddButton || newAddButton.hasAttribute('disabled');
          this.updateButtonsState(isDisabled, {
            updateText: true,
            text: isDisabled ? FoxTheme.variantStrings.soldOut : null,
          });

          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });

          // Only once the fetch settles: it carries the full variant, the optimistic path only the trimmed cache shape.
          document.dispatchEvent(
            new CustomEvent('variant:changed', {
              detail: {
                variant: variant,
              },
            })
          );

          return variant;
        };
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        // A combined-listing value's product_url can already pin a variant (`?variant=`): Shopify adds it
        // when the current selection does not exist on that product, so it must win over option_values.
        const pinsVariant = url.includes('variant=');
        if (optionValues.length && !pinsVariant) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}${url.includes('?') ? '&' : '?'}${params.join('&')}`;
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      renderProductInfo({ requestUrl, targetId, callback, standardSelect = null }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const variant = callback(html);
            // The callbacks return the matched variant, or nothing when the combination does not exist
            standardSelect?.handles.resolve({
              variant: FoxTheme.StandardEvents.variantToStandard(variant, standardSelect.selectedOptions),
            });
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            // A newer selection aborts this fetch and settles its own event: AbortError is a cancellation
            standardSelect?.handles.reject(error);
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      updateURL(url, variantId) {
        if (this.dataset.updateUrl === 'false') return;

        const parsedUrl = new URL(url, window.location.origin);

        // Staying on the same product keeps what is already on the address (utm_*, ?view=...); a
        // combined-listing swap lands on another path and starts clean.
        if (parsedUrl.pathname === window.location.pathname) {
          new URLSearchParams(window.location.search).forEach((value, key) => {
            if (!parsedUrl.searchParams.has(key)) parsedUrl.searchParams.set(key, value);
          });
        }

        if (variantId) {
          parsedUrl.searchParams.set('variant', variantId);
        } else {
          parsedUrl.searchParams.delete('variant');
        }

        window.history.replaceState({}, '', parsedUrl);
      }

      updateVariantInputs(variantId) {
        document
          .querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`)
          .forEach((productForm) => {
            const input = productForm.querySelector('input[name="id"]');
            input.value = variantId ?? '';
            input.dispatchEvent(new Event('change', { bubbles: true }));
          });
      }

      updateMedia(variant, html) {
        const productMedia = this.querySelector(`[id^="MediaGallery-${this.dataset.section}"]`);
        if (!productMedia) return; // Early return if productMedia is not found

        // With hide_variant_media the server renders a different set of media per variant: the variant
        // JSON carries no shared/filtered media list to rebuild it from, so the fetched section is the
        // only source and the gallery is swapped for it. Only when the set changed: an option the media
        // don't depend on (a size) would otherwise rebuild the sliders for the same gallery.
        if (productMedia.dataset.hideVariantMedia === 'true' && html && this.swapMediaGallery(productMedia, html)) {
          return;
        }

        const setActiveMedia = () => {
          if (typeof productMedia.setActiveMedia === 'function') {
            productMedia.init();
            productMedia.setActiveMedia(variant, { skipScroll: this.keepsPageScroll === true });
            return true; // Indicate success
          }
          return false; // Indicate failure
        };

        clearInterval(this.mediaTimer);
        if (!setActiveMedia()) {
          this.mediaTimer = setInterval(() => {
            if (setActiveMedia()) {
              clearInterval(this.mediaTimer);
            }
          }, 100);
        }
      }

      swapMediaGallery(productMedia, html) {
        const newProductMedia = html.querySelector(`[id^="MediaGallery-${this.sectionId}"]`);
        if (!newProductMedia || this.getMediaIds(productMedia) === this.getMediaIds(newProductMedia)) return false;

        clearInterval(this.mediaTimer);
        const gallery = newProductMedia.cloneNode(true);
        productMedia.replaceWith(gallery);
        // Before init: Swiper measures the slides while the layout classes still describe the old media.
        this.updateMediaCountClasses(gallery);
        // connectedCallback would wait on an IntersectionObserver tick before initialising.
        gallery.init?.();
        window?.ProductModel?.loadShopifyXR();
        return true;
      }

      // Order-independent: slides moved out of the deferred template land in a different order.
      getMediaIds(gallery) {
        const selector = '.product__media-item:not(.swiper-slide-duplicate)';
        const items = [...gallery.querySelectorAll(selector)];
        gallery.querySelectorAll('template[id^="DeferredSlides"]').forEach((template) => {
          items.push(...template.content.querySelectorAll(selector));
        });

        return items
          .map((item) => item.dataset.mediaId)
          .sort()
          .join(',');
      }

      // Mirrors the classes the section renders from the filtered media count. With the setting off
      // every variant shares one gallery, so they never change and are left as rendered.
      updateMediaCountClasses(gallery) {
        const product = this.classList.contains('product') ? this : this.querySelector('.product');
        if (!product) return;

        const count = parseInt(gallery.dataset.totalMediaCount) || 0;

        product.classList.toggle('product--media-has-many', count > 1);
        // Only quick-view and featured-product render the numbered class; main-product does not.
        const numbered = [...product.classList].find((name) => /^product--media-has-\d+$/.test(name));
        if (numbered) product.classList.replace(numbered, `product--media-has-${count}`);
        if (product.classList.contains('product--grid-mix')) {
          product.classList.toggle('product--odd-media', count % 3 === 2);
        }
      }

      updateShareUrl(variantId) {
        if (!variantId) return;
        const shareButton = document.getElementById(`ProductShare-${this.dataset.section}`);
        if (!shareButton || !shareButton.updateUrl) return;
        shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${variantId}`);
      }

      updateButtonsState(disabled, options = {}) {
        const { updateText = false, text = null, updateStyles = true } = options;

        // Handle main add button in product form
        const productForm = document.getElementById(`product-form-${this.dataset.section}`);
        if (productForm) {
          const addButton = productForm.querySelector('[name="add"]');
          const addButtonText = productForm.querySelector('[name="add"] > span');

          if (addButton) {
            if (disabled) {
              addButton.setAttribute('disabled', 'disabled');
              if (updateText && text && addButtonText) {
                addButtonText.textContent = this.decoded(text);
              }
            } else {
              const variantId = productForm.querySelector('[name="id"]')?.value;
              if (variantId) {
                addButton.removeAttribute('disabled');
                if (updateText && addButtonText) {
                  addButtonText.textContent = this.decoded(FoxTheme.variantStrings.addToCart);
                }
              }
            }

            if (updateStyles) {
              addButton.style.pointerEvents = disabled ? 'none' : '';
              addButton.style.opacity = disabled ? '0.6' : '';
            }
          }

          // The dynamic checkout button reads the same variant id but only disables itself for a sold-out
          // variant: while a selection is pending, or has no variant, the id is still the previous
          // variant's (or empty) and Buy it now would check that out.
          productForm.querySelector('.shopify-payment-button')?.toggleAttribute('inert', disabled);
        }

        // Handle sticky ATC button
        const otherButtonSelectors = ['.sticky-atc-bar [name="add"]'];
        otherButtonSelectors.forEach((selector) => {
          const buttons = document.querySelectorAll(selector);
          buttons.forEach((button) => {
            if (disabled) {
              button.setAttribute('disabled', 'disabled');
              if (updateStyles) {
                button.style.pointerEvents = 'none';
                button.style.opacity = '0.6';
              }
            } else {
              button.removeAttribute('disabled');
              if (updateStyles) {
                button.style.pointerEvents = '';
                button.style.opacity = '';
              }
            }
          });
        });
      }

      decoded(text) {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = text;
        const decoded = tempDiv.textContent;
        return decoded;
      }

      setUnavailable() {
        // The cache can settle "no such variant" before the fetch comes back with the same answer.
        if (this.isUnavailable) return;
        this.isUnavailable = true;

        this.updateButtonsState(true, {
          updateText: true,
          text: FoxTheme.variantStrings.unavailable,
        });
        const stickyAtcBar = document.getElementById(`shopify-section-sticky-atc-bar`);
        if (stickyAtcBar) stickyAtcBar.classList.add('hidden');

        // Everything that describes the previous variant (its price, a "Save 20%" badge, quantity rules and
        // volume pricing). The next real variant's render shows them again.
        ['price', 'Inventory', 'Sku', 'Badges', 'QuantityRules', 'Volume', 'PricePerItem'].forEach((id) =>
          document.getElementById(`${id}-${this.dataset.section}`)?.classList.add('hidden')
        );

        // A text still waiting for its frame (writeLiveRegion) would land in a region just hidden.
        cancelAnimationFrame(document.getElementById(`Inventory-${this.dataset.section}`)?.liveRegionFrame);
        cancelAnimationFrame(
          document.getElementById(`Sku-${this.dataset.section}`)?.querySelector('p')?.liveRegionFrame
        );

        // So the sticky bars stop offering the previous variant: they read variant: null as unavailable.
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, {
          data: {
            sectionId: this.sectionId,
            html: null,
            variant: null,
          },
        });
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.setQuantityBoundries();
        if (!this.hasAttribute('data-original-section')) {
          this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
            FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this)
          );
        }
      }

      setQuantityBoundries() {
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityBoundries, {
          data: {
            sectionId: this.sectionId,
            productId: this.productId,
          },
        });
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.productIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart')?.classList.add('btn--loading');

        fetch(`${this.getAttribute('data-url')}?variant=${currentVariantId}&section_id=${this.sectionId}`)
          .then((response) => response.text())
          .then((responseText) => {
            const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.sectionId, this.productId, parsedHTML);
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            this.querySelector('.quantity__rules-cart')?.classList.remove('btn--loading');
          });
      }

      updateQuantityRules(sectionId, productId, parsedHTML) {
        if (!this.quantityInput) return;

        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityRules, {
          data: {
            sectionId,
            productId,
            parsedHTML,
          },
        });

        this.setQuantityBoundries();
      }

      getVariantsCache() {
        const script = this.querySelector('variant-selects [data-variants-cache]');
        if (!script) return null;

        // Re-parse when variant-selects has been replaced (combined-listing swap, editor re-render).
        if (this.cachedVariantsSource !== script) {
          try {
            this.cachedVariants = JSON.parse(script.textContent);
          } catch (error) {
            console.error('Failed to parse the variants cache:', error);
            this.cachedVariants = null;
          }
          this.cachedVariantsSource = script;
        }

        return this.cachedVariants;
      }

      // Set by product-variant-picker.liquid: false for the option-value neighbourhood of a product past
      // 250 variants, whose length says nothing about whether it holds every variant.
      isVariantsCacheComplete() {
        return this.querySelector('variant-selects [data-variants-cache]')?.dataset.complete === 'true';
      }

      // The same nodes variant-selects reads for selectedOptionValues, so the two never disagree.
      //
      // Scoped to the current picker rather than to this element: viewTransition swaps the picker by
      // inserting the new node before the old one and removing the old only 500ms later, so for that
      // window a query from here spans both and returns one selection appended to another — twice as
      // many values as the product has options, or a mix of two selections if the shopper clicks
      // again meanwhile. Every optimistic path then fails its length check and the click falls back
      // to the network, which is what makes the picker feel stuck mid-switch.
      getSelectedOptionValues() {
        const picker = this.variantSelectors;
        if (!picker) return [];

        return Array.from(picker.querySelectorAll('select option[selected], fieldset input:checked')).map(
          (element) => element.value
        );
      }

      matchesOptions(variant, selectedValues) {
        return (
          selectedValues.length > 0 &&
          variant.options.length === selectedValues.length &&
          variant.options.every((option, index) => option === selectedValues[index])
        );
      }

      getVariantFromCache() {
        const variants = this.getVariantsCache();
        if (!variants) return null;

        const selectedValues = this.getSelectedOptionValues();

        return variants.find((variant) => this.matchesOptions(variant, selectedValues)) || null;
      }

      // The cache could not answer, which on a product past 250 variants is every click. Ask the
      // server for that one variant instead of waiting on the section render: still a round trip, but
      // a few hundred bytes rather than the whole section, so price and button land noticeably sooner.
      fetchVariantData(productUrl, optionValues) {
        if (!optionValues.length) return;

        this.variantDataAbortController?.abort();
        this.variantDataAbortController = new AbortController();

        const token = {};
        this.variantDataToken = token;

        fetch(`${productUrl}?section_id=variant-data&option_values=${optionValues.join(',')}`, {
          signal: this.variantDataAbortController.signal,
        })
          .then((response) => (response.ok ? response.text() : Promise.reject(new Error(response.status))))
          .then((responseText) => {
            // A newer click owns the UI by now; this answer describes a variant nobody is looking at.
            if (this.variantDataToken !== token) return;

            const json = new DOMParser().parseFromString(responseText, 'text/html').body.textContent.trim();
            const variant = json ? JSON.parse(json) : null;

            // Shopify resolves option_values server-side, but a set it cannot resolve falls back to
            // the default variant rather than to nothing — a bogus option value id comes back as the
            // current variant, not null. So patch only when the answer describes the combination that
            // was actually asked for; anything else is left to the render and its setUnavailable.
            if (variant && this.matchesOptions(variant, this.getSelectedOptionValues())) {
              this.applyOptimisticUpdate(variant, productUrl);
            }
          })
          .catch((error) => {
            if (error.name !== 'AbortError') console.error('Variant data fetch failed:', error);
          });
      }

      applyOptimisticUpdate(variant, productUrl) {
        // Before updateButtonsState: it only re-enables once the form carries a variant id.
        this.updateVariantInputs(variant.id);

        // Quantity rules (B2B) give the quantity input its min/step/max from the variant and from what is
        // already in the cart, which only the render knows. Until it lands, a non-default rule on either
        // side would let the current input add a quantity the new variant refuses, so the buttons stay
        // pending; everything else still updates now.
        const awaitsQuantityRules =
          !this.isDefaultQuantityRule(variant.quantity_rule) ||
          !this.isDefaultQuantityRule(this.getInputQuantityRule());
        if (variant.available) {
          // The text too: the previous variant may have left "Sold out" on a button now merely pending.
          this.updateButtonsState(awaitsQuantityRules, { updateText: true, text: FoxTheme.variantStrings.addToCart });
        } else {
          this.updateButtonsState(true, { updateText: true, text: FoxTheme.variantStrings.soldOut });
        }

        // Only ever reached for the product already on screen (a combined-listing swap skips the
        // optimistic path), so this can only set ?variant=, never change the path.
        this.updateURL(productUrl, variant.id);

        // setUnavailable() hides the price when no variant matches the selection; a real variant is known
        // here, so it belongs visible again regardless of volume pricing, which only decides whether its
        // content is rewritten below or left to the fetch.
        const price = this.querySelector(`#price-${this.dataset.section}`);
        price?.classList.remove('hidden');

        // Same for the sticky bar setUnavailable() hid, and the unavailable state it set.
        this.isUnavailable = false;
        document.getElementById('shopify-section-sticky-atc-bar')?.classList.remove('hidden');

        // Quantity price breaks render a price range the variant JSON cannot express. #VolumeNote-
        // exists only in that case, so its presence is the signal to leave the price to the fetch.
        const hasVolumePricing = !!this.querySelector(`#VolumeNote-${this.dataset.section}`);
        if (!hasVolumePricing) {
          FoxTheme.updatePriceFromVariant(price, variant);
        }

        // hide_variant_media galleries are left to the fetch: the media they show depends on the
        // server-rendered list, which the trimmed cache shape does not carry.
        const productMedia = this.querySelector(`[id^="MediaGallery-${this.dataset.section}"]`);
        if (variant.featured_media && productMedia?.dataset.hideVariantMedia !== 'true') {
          this.updateMedia(variant);
        }

        // Everything else the variant JSON can answer, so none of it sits on the previous variant until
        // the render lands. Pickup availability still needs its own request, but it starts now.
        this.updateBadges(variant);
        this.updateInventory(variant);
        this.updateVariantMeta('Sku', variant.sku);
        this.updateShareUrl(variant.id);
        this.pickupAvailability?.update(variant);

        // Publishes the same event handleUpdateProductInfo does once the fetch settles, just sooner:
        // every subscriber only reads fields already on the trimmed shape. `html: null` mirrors "not
        // fetched yet". Skipped for volume pricing for the same reason as the price patch above:
        // price-per-item.js recomputes its table on every variantChange.
        if (!hasVolumePricing) {
          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html: null,
              variant,
              awaitsQuantityRules,
            },
          });
        }
      }

      // Shopify's rule for a variant without one: min 1, step 1, no max. A missing rule (no quantity input,
      // an older cache) reads as the default.
      isDefaultQuantityRule(rule) {
        return !rule || (Number(rule.min) === 1 && Number(rule.increment) === 1 && rule.max == null);
      }

      // The rule the quantity input was rendered with (buy-buttons.liquid), before any cart adjustment.
      getInputQuantityRule() {
        const input = this.quantityInput;
        if (!input) return null;

        return {
          min: input.dataset.min,
          max: input.dataset.max ?? null,
          increment: input.getAttribute('step'),
        };
      }

      // Mirrors product-badges.liquid, from the config it renders (data-badges-config): the tag badges and a
      // sale badge while the variant is in stock, the sold-out badge otherwise. Done on the click like the
      // price: the badge row sits above the variant picker, and changed only once the render lands it would
      // move the picker while the shopper is already aiming at the next option.
      updateBadges(variant) {
        const badges = this.querySelector(`#Badges-${this.dataset.section}`);
        if (!badges?.dataset.badgesConfig || variant.in_stock === undefined) return;

        let config;
        try {
          config = JSON.parse(badges.dataset.badgesConfig);
        } catch (error) {
          return;
        }

        let html = '';
        if (variant.in_stock) {
          html = config.tags;
          const { price, compare_at_price } = variant;
          if (config.showSale && compare_at_price > price) {
            let text = config.onSaleText;
            if (config.saleType === 'percentage') {
              // Liquid's integer divided_by floors.
              text = config.saveText.replace(
                '[amount]',
                `${Math.floor(((compare_at_price - price) * 100) / compare_at_price)}%`
              );
            } else if (config.saleType === 'fixed_amount') {
              text = config.saveText.replace(
                '[amount]',
                FoxTheme.Currency.formatMoney(compare_at_price - price, config.moneyFormat)
              );
            }
            html += `<span class="f-badge f-badge--sale">${text}</span>`;
          }
        } else if (config.showSoldout) {
          html = `<span class="f-badge f-badge--soldout">${config.soldOutText}</span>`;
        }

        badges.innerHTML = html;
        // setUnavailable() hides the row for a combination that doesn't exist.
        badges.classList.remove('hidden');
      }

      // Mirrors the inventory block in product-information-blocks.liquid, from the status variant-json.liquid
      // computed against that block's threshold. Same reason as the badges: it sits above the picker, and its
      // low-stock bar comes and goes with the variant.
      updateInventory(variant) {
        const inventory = this.querySelector(`#Inventory-${this.dataset.section}`);
        if (!inventory?.dataset.inventoryConfig || !variant.inventory) return;

        let config;
        try {
          config = JSON.parse(inventory.dataset.inventoryConfig);
        } catch (error) {
          return;
        }

        const { status, quantity } = variant.inventory;
        if (status === 'untracked') {
          cancelAnimationFrame(inventory.liveRegionFrame);
          inventory.replaceChildren();
          inventory.classList.add('hidden');
          return;
        }

        const withQuantity = (text) => text.replace('[quantity]', quantity);
        const messages = {
          'low-stock': config.showQuantity
            ? withQuantity(quantity === 1 ? config.lowStockOne : config.lowStockMany)
            : config.lowStock,
          'in-stock': config.showQuantity ? withQuantity(config.inStockCount) : config.inStock,
          continue: config.continueSelling,
          'out-of-stock': config.outOfStock,
        };
        // A continue-selling variant out of stock reads "in stock" (its class too), as in Liquid.
        const statusClass = status === 'continue' ? 'in-stock' : status;

        let html = `<p class="product__inventory product__inventory--${statusClass}"><span class="product__inventory-icon"></span><span class="product__inventory-text">${messages[status]}</span></p>`;
        if (status === 'low-stock' && quantity !== null && quantity < config.threshold) {
          html += `<progress-bar class="product__inventory-stock-bar progress-bar" data-value="${quantity}" data-max="${config.threshold}"></progress-bar>`;
        }

        this.writeLiveRegion(inventory, inventory, () => {
          inventory.innerHTML = html;
        });
      }

      // A live region that appears together with its text is often not announced: when the region was hidden,
      // show it first and write its text a frame later.
      writeLiveRegion(region, wrapper, write) {
        cancelAnimationFrame(region.liveRegionFrame);
        const wasHidden = wrapper.classList.contains('hidden');
        wrapper.classList.remove('hidden');

        if (wasHidden) {
          region.liveRegionFrame = requestAnimationFrame(write);
        } else {
          write();
        }
      }

      // Mirrors the SKU markup in product-information-blocks.liquid: a visually hidden label and the value,
      // or empty and hidden when the variant has none.
      updateVariantMeta(id, value) {
        const meta = this.querySelector(`#${id}-${this.dataset.section}`);
        const text = meta?.querySelector('p');
        if (!text || value === undefined) return;

        const label = document.createElement('span');
        label.className = 'visually-hidden';
        label.textContent = `${meta.dataset.label}:`;

        if (!value) {
          cancelAnimationFrame(text.liveRegionFrame);
          text.replaceChildren();
          meta.classList.add('hidden');
          return;
        }

        this.writeLiveRegion(text, meta, () => text.replaceChildren(label, value));
      }

      // Text and classes rather than markup: the optimistic patch writes the same price with different
      // whitespace and class order than the Liquid render. data-value too: a low-stock bar's quantity.
      getRenderedSignature(node) {
        const classes = Array.from(
          node.querySelectorAll('*'),
          (element) => [...element.classList].sort().join('.') + (element.dataset.value ?? '')
        );
        return `${classes.join('|')}#${node.textContent.replace(/\s+/g, '')}`;
      }

      disableButtons() {
        this.updateButtonsState(true);
      }

      enableButtons() {
        this.updateButtonsState(false);
      }
    }
  );
}
